import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  api,
  clearTokens,
  hasSession,
  setSessionLostHandler,
  setTokens,
} from '@/shared/api/client';
import type { LoginResponse, SessionView } from '@/shared/api/types';
import { applyBranding } from '@/shared/branding/applyBranding';

import { AuthContext, type AuthContextValue, type AuthStatus } from './context';

export function AuthProvider({ children }: { children: ReactNode }) {
  // O estado inicial ja reflete o que se sabe na montagem. Comecar sempre em
  // carregamento e corrigir depois dentro de um efeito produziria uma renderizacao
  // desperdicada e um piscar da tela de acesso.
  const [status, setStatus] = useState<AuthStatus>(() =>
    hasSession() ? 'loading' : 'anonymous',
  );
  const [session, setSession] = useState<SessionView | null>(null);

  const clearSession = useCallback(() => {
    clearTokens();
    setSession(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    setSessionLostHandler(clearSession);
  }, [clearSession]);

  useEffect(() => {
    // O token de acesso vive em memoria e nao sobrevive ao recarregamento da
    // pagina. Sem sessao em memoria, o usuario ja nasce anonimo pelo estado
    // inicial. A persistencia entre recarregamentos passa a existir quando o
    // token de renovacao migrar para cookie de servidor.
    // Registrado em docs/OPEN_QUESTIONS.md.
    if (!hasSession()) {
      return;
    }

    let cancelled = false;
    api
      .get<SessionView>('/api/v1/auth/session')
      .then((restored) => {
        if (cancelled) return;
        setSession(restored);
        applyBranding(restored.tenant.branding);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!cancelled) clearSession();
      });

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const signIn = useCallback(
    async (email: string, password: string, tenantSlug?: string) => {
      const payload: Record<string, string> = { email, password };
      if (tenantSlug) {
        payload['tenant_slug'] = tenantSlug;
      }

      const result = await api.post<LoginResponse>('/api/v1/auth/login', payload, {
        skipAuth: true,
      });

      setTokens(result.tokens.access_token, result.tokens.refresh_token);
      setSession(result.session);
      applyBranding(result.session.tenant.branding);
      setStatus('authenticated');
    },
    [],
  );

  // A troca de empresa devolve um par de tokens novo e uma sessao nova, com outro
  // vinculo e outro conjunto de permissoes. A sessao anterior e encerrada pelo
  // servidor: a aba esquecida na outra empresa nao continua valendo.
  const switchTenant = useCallback(async (tenantSlug: string) => {
    const result = await api.post<LoginResponse>('/api/v1/auth/switch-tenant', {
      tenant_slug: tenantSlug,
    });

    setTokens(result.tokens.access_token, result.tokens.refresh_token);
    setSession(result.session);
    applyBranding(result.session.tenant.branding);
  }, []);

  // A troca de corretora do assessor (P-ACE-2) muda o escopo da mesma sessao: os
  // tokens continuam, e a sessao devolvida ja vem com a corretora escolhida.
  const switchBroker = useCallback(async (brokerId: string) => {
    const atualizada = await api.post<SessionView>('/api/v1/auth/switch-broker', {
      broker_id: brokerId,
    });
    setSession(atualizada);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post<void>('/api/v1/auth/logout');
    } finally {
      // A sessao local e encerrada mesmo que a chamada falhe. Manter o usuario
      // preso a uma sessao que ele pediu para encerrar seria pior do que deixar
      // um registro orfao no servidor, que expira sozinho.
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set(session?.permissions ?? []);
    const features = new Set(session?.features ?? []);

    return {
      status,
      session,
      signIn,
      signOut,
      switchTenant,
      switchBroker,
      can: (permission: string) => permissions.has(permission),
      hasFeature: (feature: string) => features.has(feature),
    };
  }, [status, session, signIn, signOut, switchTenant, switchBroker]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
